// src/components/MealDetailView.js

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Alert } from '../../../shared/components/ui';
import { useTranslation } from 'react-i18next';
import { Clock, ChefHat, ArrowLeft, UtensilsCrossed } from 'lucide-react';
import { getUnsplashImageUrlSync, getMainCategory } from '../../../shared/utils/unsplashUtils';
import RecipeOptionsButton from './RecipeOptionsButton';
import { recipeService } from '../services/recipeService';
import EditRecipeModal from './EditRecipeModal';
import { useRecipeSave } from '../hooks/useRecipeSave';

const MealDetailView = ({ meal, showBackButton = false, onBack, onRecipeUpdated }) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [recipe, setRecipe] = useState(meal);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [editError, setEditError] = useState('');
    const { saveRecipe } = useRecipeSave();
    
    useEffect(() => {
        setRecipe(meal);
    }, [meal]);

    if (!meal) return null;

    // Mapear meal a recipe si es necesario
    const recipeData = recipe || meal;

    // Usar imagen del backend si existe, sino usar placeholder de Unsplash
    let image = recipeData.image_url;
    if (!image) {
        // Determinar categoría y obtener imagen de placeholder
        const mainCategory = getMainCategory(recipeData.ingredients || []);
        image = getUnsplashImageUrlSync(mainCategory);
    }

    // Calcular dificultad y tiempo
    const ingredientCount = recipeData.ingredients?.length || 0;
    const stepCount = recipeData.steps?.length || 0;
    const totalComplexity = ingredientCount + stepCount;
    
    let difficulty = 'Medium';
    if (totalComplexity <= 5) {
        difficulty = 'Easy';
    } else if (totalComplexity >= 15) {
        difficulty = 'Hard';
    }

    let cookTime = null;
    if (stepCount > 0) {
        const estimatedMinutes = Math.max(15, stepCount * 5);
        cookTime = `${estimatedMinutes} min`;
    }

    const getDifficultyColor = (level) => {
        const colors = {
            Easy: 'text-green-600 bg-green-50',
            Medium: 'text-yellow-600 bg-yellow-50',
            Hard: 'text-red-600 bg-red-50'
        };
        return colors[level] || colors.Medium;
    };

    const translateDifficulty = (level) => {
        return t(`recipe.difficultyLevels.${level}`, { defaultValue: level });
    };

    const handleToggleFavorite = async (recipeId) => {
        try {
            await recipeService.toggleRecipeFavorite(recipeId);
            setRecipe(prev => ({ ...prev, is_favorite: !prev.is_favorite }));
        } catch (err) {
            // Error handling
        }
    };

    const handleEditRecipe = async (recipeData) => {
        setEditError('');

        try {
            const response = await recipeService.getRecipeById(recipeData.id);
            if (response.success) {
                setRecipe(response.recipe);
                setIsEditModalOpen(true);
            } else {
                setEditError(t('recipe.library.loadError') || t('common.error'));
            }
        } catch (err) {
            setEditError(err.message || t('recipe.library.loadError') || t('common.error'));
        }
    };

    const handleSaveRecipe = async (formData) => {
        const currentId = (recipe && recipe.id) || (meal && meal.id);

        if (!currentId) {
            return;
        }

        await saveRecipe(formData, currentId, async (updatedRecipe) => {
            setRecipe(updatedRecipe);
            if (onRecipeUpdated) {
                onRecipeUpdated(updatedRecipe);
            }
        });
    };

    const handleCloseEditModal = () => {
        setIsEditModalOpen(false);
    };

    const handleArchive = async (recipeId) => {
        try {
            await recipeService.archiveRecipe(recipeId);
            if (onBack) onBack();
        } catch (err) {
            // Error handling
        }
    };

    return (
        <div className="space-y-6">
            {editError && (
                <Alert variant="error">
                    <p>{editError}</p>
                </Alert>
            )}

            {/* Botón de volver */}
            {showBackButton && onBack && (
                <Button
                    variant="ghost"
                    size="sm"
                    onClick={onBack}
                    className="text-gray-600 hover:text-gray-800 mb-4"
                >
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    {t('meal.details.back') || t('common.back')}
                </Button>
            )}

            {/* Hero Section - Imagen grande */}
            <div className="relative rounded-2xl overflow-hidden shadow-xl">
                <div className="relative h-96 overflow-hidden bg-gradient-to-br from-primary-50 to-primary-100">
                    {image ? (
                        <img
                            src={image}
                            alt={recipeData.name}
                            className="w-full h-full object-cover"
                        />
                    ) : (
                        <div className="w-full h-full flex items-center justify-center">
                            <ChefHat className="w-32 h-32 text-primary-300" />
                        </div>
                    )}

                    {/* Overlay gradient */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" />

                    {/* Difficulty badge */}
                    <div className="absolute top-6 left-6">
                        <span className={`px-4 py-2 rounded-full text-sm font-semibold backdrop-blur-sm ${getDifficultyColor(difficulty)}`}>
                            {translateDifficulty(difficulty)}
                        </span>
                    </div>

                    {/* Options button */}
                    <div className="absolute top-6 right-6 z-10">
                        <RecipeOptionsButton
                            recipe={recipeData}
                            onToggleFavorite={handleToggleFavorite}
                            onEditRecipe={handleEditRecipe}
                            onArchive={handleArchive}
                        />
                    </div>

                    {/* Title overlay */}
                    <div className="absolute bottom-0 left-0 right-0 p-8">
                        <h1 className="text-4xl md:text-5xl font-bold text-white mb-3 drop-shadow-lg">
                            {recipeData.name}
                        </h1>
                        
                        {/* Metadata */}
                        <div className="flex flex-wrap items-center gap-4 text-white/90">
                            {cookTime && (
                                <div className="flex items-center gap-2">
                                    <Clock className="w-5 h-5" />
                                    <span className="text-lg font-medium">{cookTime}</span>
                                </div>
                            )}
                            {ingredientCount > 0 && (
                                <div className="flex items-center gap-2">
                                    <UtensilsCrossed className="w-5 h-5" />
                                    <span className="text-lg font-medium">{ingredientCount} {t('meal.details.ingredients')?.toLowerCase() || 'ingredientes'}</span>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Content Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Main Content */}
                <div className="lg:col-span-2 space-y-6">
                    {/* Ingredients */}
                    {recipeData.ingredients && recipeData.ingredients.length > 0 && (
                        <div className="bg-white rounded-2xl shadow-lg p-6">
                            <h2 className="text-2xl font-bold text-gray-800 mb-4 flex items-center gap-2">
                                <UtensilsCrossed className="w-6 h-6 text-primary-600" />
                                {t('meal.details.ingredients')}
                            </h2>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                {recipeData.ingredients.map((ingredient, index) => (
                                    <div
                                        key={index}
                                        className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                                    >
                                        <span className="text-gray-800 font-medium">
                                            {ingredient.item}
                                        </span>
                                        {ingredient.quantity && (
                                            <span className="text-gray-600 text-sm ml-2">
                                                {ingredient.quantity}
                                            </span>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* No steps message */}
                    {(!recipeData.steps || recipeData.steps.length === 0) && (
                        <div className="bg-white rounded-2xl shadow-lg p-12 text-center">
                            <ChefHat className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                            <p className="text-gray-500 text-lg">{t('meal.details.noSteps') || 'No hay pasos de cocina disponibles'}</p>
                        </div>
                    )}
                </div>

                {/* Sidebar */}
                <div className="space-y-6">
                    {/* Info Card */}
                    <div className="bg-white rounded-2xl shadow-lg p-6">
                        <h3 className="text-lg font-bold text-gray-800 mb-4">{t('meal.details.info')}</h3>
                        <div className="space-y-3">
                            {difficulty && (
                                <div className="flex items-center justify-between">
                                    <span className="text-gray-600">{t('recipe.difficulty')}:</span>
                                    <span className={`px-3 py-1 rounded-full text-sm font-semibold ${getDifficultyColor(difficulty)}`}>
                                        {translateDifficulty(difficulty)}
                                    </span>
                                </div>
                            )}
                            {cookTime && (
                                <div className="flex items-center justify-between">
                                    <span className="text-gray-600">{t('recipe.cookTime')}:</span>
                                    <span className="font-medium text-gray-800">{cookTime}</span>
                                </div>
                            )}
                            {ingredientCount > 0 && (
                                <div className="flex items-center justify-between">
                                    <span className="text-gray-600">{t('recipe.ingredients')}:</span>
                                    <span className="font-medium text-gray-800">{ingredientCount}</span>
                                </div>
                            )}
                            {stepCount > 0 && (
                                <div className="flex items-center justify-between">
                                    <span className="text-gray-600">{t('recipe.steps')}:</span>
                                    <span className="font-medium text-gray-800">{stepCount}</span>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Steps - Full width row */}
            {recipeData.steps && recipeData.steps.length > 0 && (
                <div className="bg-white rounded-2xl shadow-lg p-6">
                    <h2 className="text-2xl font-bold text-gray-800 mb-6 flex items-center gap-2">
                        <ChefHat className="w-6 h-6 text-primary-600" />
                        {t('meal.details.steps')}
                    </h2>
                    <div className="space-y-4">
                        {recipeData.steps.map((step, index) => {
                            const stepContent = typeof step === 'string' ? step : (step.content || step.instruction || step);
                            const stepOrder = typeof step === 'string' ? index + 1 : (step.order || step.step || index + 1);
                            const stepKey = typeof step === 'string' ? index : (step.id || index);

                            return (
                                <div
                                    key={stepKey}
                                    className="flex gap-4 p-4 bg-gradient-to-r from-gray-50 to-white rounded-xl border border-gray-100 hover:shadow-md transition-shadow"
                                >
                                    <div className="flex-shrink-0 w-10 h-10 bg-primary-600 text-white rounded-full flex items-center justify-center text-lg font-bold">
                                        {stepOrder}
                                    </div>
                                    <div className="flex-1 pt-1">
                                        <p className="text-gray-700 leading-relaxed text-base">
                                            {stepContent}
                                        </p>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            <EditRecipeModal
                isOpen={isEditModalOpen}
                onClose={handleCloseEditModal}
                recipe={recipe}
                onSave={handleSaveRecipe}
            />
        </div>
    );
};

export default MealDetailView;
